// Admin-only API client for the hidden golden set. Operators do NOT see any
// of this — golden assignments are mixed into normal random distribution by
// the backend scheduler (see Phase 3) and committed through the regular
// /commit endpoint with an opaque assignment token.

import { fetchJSON } from './api';
import { apiPath } from './constants';

// ── Types (mirror workers/src/types.ts) ──────────────────────────────────

export type GoldenSetStatus = 'draft' | 'active' | 'paused' | 'closed' | 'archived';
export type GoldenAttemptStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';
// 'audit' = snapshot from an existing audit row; 'vote' = no audit so we fell
// back to a vote. The GT operator name is stored separately per-episode and is
// fully configurable per set.
export type GoldenGroundTruthSource = 'vote' | 'audit';

export interface GoldenSetSummary {
  set_id: string;
  name: string;
  description: string | null;
  status: GoldenSetStatus;
  created_by: string;
  created_at_ms: number;
  published_at_ms: number | null;
  closed_at_ms: number | null;
  feedback_released_at_ms: number | null;
  target_ratio: number | null;
  max_per_operator_per_day: number | null;
  episode_count: number;
  participant_count: number;
  attempts_total: number;
  attempts_completed: number;
}

export interface GoldenSetListResponse {
  sets: GoldenSetSummary[];
}

export interface GoldenPreviewValidRow {
  episode_id: string;
  duration: number;
  ground_truth_source: GoldenGroundTruthSource;
  ground_truth_operator: string;
}

export interface GoldenPreviewInvalidRow {
  episode_id: string;
  reason: string;
}

export interface GoldenPreviewResponse {
  valid: GoldenPreviewValidRow[];
  invalid: GoldenPreviewInvalidRow[];
}

export interface GoldenPublishResponse {
  set_id: string;
  valid_episodes: number;
  invalid_episodes: GoldenPreviewInvalidRow[];
  participants: number;
  attempts_created: number;
}

export interface GoldenSetDetail {
  set: {
    set_id: string;
    name: string;
    description: string | null;
    status: GoldenSetStatus;
    created_by: string;
    created_at_ms: number;
    published_at_ms: number | null;
    closed_at_ms: number | null;
    feedback_released_at_ms: number | null;
    target_ratio: number | null;
    max_per_operator_per_day: number | null;
  };
  episodes: Array<{
    episode_id: string;
    ground_truth_source: GoldenGroundTruthSource;
    ground_truth_operator: string;
    duration: number;
  }>;
  participants: Array<{ operator: string; assigned_at_ms: number; active: boolean }>;
  attempts_by_status: Record<GoldenAttemptStatus, number>;
}

// ── Admin endpoints ──────────────────────────────────────────────────────

export async function previewGoldenSet(
  episodeIds: string[],
  groundTruthOperators: string[]
): Promise<GoldenPreviewResponse | null> {
  return fetchJSON<GoldenPreviewResponse>(apiPath('/admin/golden/preview'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      episode_ids: episodeIds,
      ground_truth_operators: groundTruthOperators,
    }),
  });
}

export async function publishGoldenSet(input: {
  name: string;
  description: string | null;
  target_ratio: number | null;
  max_per_operator_per_day: number | null;
  ground_truth_operators: string[];
  episode_ids: string[];
  participants: string[];
}): Promise<GoldenPublishResponse | null> {
  return fetchJSON<GoldenPublishResponse>(apiPath('/admin/golden/sets'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function listGoldenSets(): Promise<GoldenSetSummary[]> {
  const resp = await fetchJSON<GoldenSetListResponse>(apiPath('/admin/golden/sets'));
  return resp?.sets ?? [];
}

export async function getGoldenSet(setId: string): Promise<GoldenSetDetail | null> {
  return fetchJSON<GoldenSetDetail>(
    `${apiPath('/admin/golden/sets')}/${encodeURIComponent(setId)}`
  );
}

export async function setGoldenSetStatus(
  setId: string,
  status: GoldenSetStatus
): Promise<{ ok: boolean; status: GoldenSetStatus } | null> {
  return fetchJSON<{ ok: boolean; status: GoldenSetStatus }>(
    `${apiPath('/admin/golden/sets')}/${encodeURIComponent(setId)}/status`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }
  );
}

export async function listGoldenOperators(): Promise<string[]> {
  const resp = await fetchJSON<{ operators: string[] }>(apiPath('/admin/golden/operators'));
  return resp?.operators ?? [];
}

// ── Phase 5: admin results + feedback release ─────────────────────────────

export interface GoldenSetResults {
  set: {
    set_id: string;
    name: string;
    status: GoldenSetStatus;
    feedback_released_at_ms: number | null;
  };
  operators: Array<{
    operator: string;
    pending: number;
    in_progress: number;
    completed: number;
    skipped: number;
    match_bits: number;
    total_bits: number;
  }>;
  episodes: Array<{
    episode_id: string;
    duration: number;
    completed_attempts: number;
    total_attempts: number;
    match_bits: number;
    total_bits: number;
  }>;
}

export type GoldenResultsCsvView = 'operators' | 'episodes';

export interface GoldenResultsRange {
  fromMs: number;
  toMs: number;
}

function appendGoldenResultsParams(
  params: URLSearchParams,
  range?: GoldenResultsRange | null,
  episodeId?: string | null
) {
  if (range) {
    params.set('from', String(range.fromMs));
    params.set('to', String(range.toMs));
  }
  if (episodeId) params.set('episode_id', episodeId);
}

export async function getGoldenSetResults(
  setId: string,
  range?: GoldenResultsRange | null,
  episodeId?: string | null
): Promise<GoldenSetResults | null> {
  const params = new URLSearchParams();
  appendGoldenResultsParams(params, range, episodeId);
  const qs = params.toString();
  return fetchJSON<GoldenSetResults>(
    `${apiPath('/admin/golden/sets')}/${encodeURIComponent(setId)}/results${qs ? `?${qs}` : ''}`
  );
}

export function goldenSetResultsCsvPath(
  setId: string,
  view: GoldenResultsCsvView,
  range?: GoldenResultsRange | null,
  episodeId?: string | null
): string {
  const params = new URLSearchParams({ view });
  appendGoldenResultsParams(params, range, episodeId);
  return `/admin/golden/sets/${encodeURIComponent(setId)}/results.csv?${params}`;
}

export async function releaseGoldenSetFeedback(
  setId: string
): Promise<{ ok: boolean; feedback_released_at_ms: number } | null> {
  return fetchJSON<{ ok: boolean; feedback_released_at_ms: number }>(
    `${apiPath('/admin/golden/sets')}/${encodeURIComponent(setId)}/release-feedback`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
}

// ── Phase 5: operator-facing quality feedback ─────────────────────────────

export interface GoldenSelfSummaryEntry {
  set_id: string;
  name: string;
  feedback_released_at_ms: number;
  attempts_completed: number;
  attempts_total: number;
  match_bits: number;
  total_bits: number;
}

export interface GoldenSelfSummary {
  sets: GoldenSelfSummaryEntry[];
  aggregate: { match_bits: number; total_bits: number; attempts_completed: number };
}

export async function getMyGoldenSummary(operator: string): Promise<GoldenSelfSummary | null> {
  return fetchJSON<GoldenSelfSummary>(
    `${apiPath('/me/quality/golden-summary')}?operator=${encodeURIComponent(operator)}`
  );
}

export interface GoldenSelfReview {
  set_id: string;
  episode_id: string;
  duration: number;
  completed_at_ms: number;
  operator_encoded: string;
  ground_truth_encoded: string;
  match_bits: number;
  total_bits: number;
}

export async function getMyGoldenReview(
  operator: string,
  setId: string,
  episodeId: string
): Promise<GoldenSelfReview | null> {
  const url = `${apiPath('/me/quality/golden-review')}/${encodeURIComponent(setId)}/${encodeURIComponent(episodeId)}?operator=${encodeURIComponent(operator)}`;
  try {
    return await fetchJSON<GoldenSelfReview>(url);
  } catch {
    return null;
  }
}

// ── CSV helpers (admin upload) ───────────────────────────────────────────
//
// We keep this client-side parser intentionally minimal: the only required
// column is `episode_id`. Handles whitespace/comma/semicolon-separated text
// (paste flow) as well as a real CSV file with a header row.

export function parseEpisodeIdsFromText(input: string): string[] {
  return Array.from(
    new Set(
      input
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    )
  );
}

export function parseEpisodeIdsFromCsv(csv: string): string[] {
  const lines = csv.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) return [];

  // Detect header: if the first line contains "episode_id" treat it as a
  // header and pick that column; otherwise assume the file is one ID per line.
  const firstLine = lines[0]!;
  const cells = firstLine.split(',').map((c) => c.trim());
  const headerIdx = cells.findIndex((c) => c.toLowerCase() === 'episode_id');

  if (headerIdx >= 0) {
    const ids = lines.slice(1).map((line) => {
      const fields = line.split(',').map((c) => c.trim());
      return fields[headerIdx] ?? '';
    });
    return Array.from(new Set(ids.filter(Boolean)));
  }

  // No header: take first column.
  const ids = lines.map((line) => line.split(',')[0]?.trim() ?? '').filter(Boolean);
  return Array.from(new Set(ids));
}
